import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControlLabel,
  Button,
  Stack,
  Typography,
  Radio,
  RadioGroup,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
  Box,
} from "@mui/material";
import { useArrayData } from "../../context/ArrayDataContext";
import { getPlannerPricingError, submitPlannerView } from "../../api/submitPlannerView";
import { useDispatch } from "react-redux";
import { setIsSubmitted, setSubmittedData } from "../../redux/actions/userSlice";
import { useSnackbar } from "../SnakBar/AppSnackbarProvider";
import { getReasonCodeList } from "../../api/reasonCodeApi";
import { getMaxCoverage, MaxCoverageLimit } from "../../api/maxCoverageApi";
import ErrorDialog from "../common/ErrorDialog/ErrorDialog";

type Choice = "IO" | "S4";

type ReasonOption = { value: string; label?: string };

type PopupWithCheckboxesProps = {
  open: boolean;
  onClose: () => void;
  selectedRows: any[];
  onSubmit?: (choice: Choice, payloads: any[], response?: any) => void;
  reasonOptions?: ReasonOption[];
  normalizedReasonOptions?: ReasonOption[];
  forceChoice?: Choice;
};

const toFieldName = (key: string) => {
  if (!key) return key as any;
  const parts = key.split(" ");
  parts[0] = parts[0].charAt(0).toLowerCase() + parts[0].slice(1);
  return parts.join("").replace(/[^\w]/g, "");
};

const toSafeString = (v: any) => {
  if (v === null || v === undefined || Number.isNaN(v)) return "";
  return String(v).trim();
};

const toSafeNumber = (v: any) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const cleaned = String(v)
    .trim()
    .replace(/,/g, "")
    .replace(/[^\d.-]/g, "");
  if (!cleaned || cleaned === "-" || cleaned === "." || cleaned === "-.") return 0;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const createPayloadFromChoice = (
  item: any,
  choice: Choice,
  idx?: number,
  overrides?: { reason?: string; comments?: string },
  normalizedReasonOptions: any[] = [],
) => {
  const idSafe =
    String(
      item?.materialNumber ??
      item?.["Material Number"] ??
      item?.id ??
      item?.Id ??
      item?.ID ??
      "",
    ).trim() || `tmp-${Date.now()}-${idx ?? 0}`;

  let submitSafetyStock = "";
  let submitCoverage = "";
  let submitSafetyTime = "";

  if (choice === "S4") {
    // Raw numeric values take priority; fall back to formatted display-name keys
    submitSafetyStock = toSafeString(
      item?.s4SafetyStockQtyRaw ?? item?.["s4SafetyStock(Qty)"],
    );
    submitCoverage = toSafeString(
      item?.s4CoverageProfileRaw ?? item?.["s4Coverageprofile(weeks)"],
    );
    submitSafetyTime = toSafeString(
      item?.s4SafetyTimeRaw ?? item?.["s4SafetyTime(weeks)"],
    );
  } else {
    // 'IO'
    submitSafetyStock = toSafeString(
      item?.ioSafetyStockQtyRaw ?? item?.["ioSafetyStockQty"],
    );
    submitCoverage = toSafeString(
      item?.ioCoverageProfileRaw ?? item?.["iOCoverageprofile(weeks)"],
    );
    submitSafetyTime = toSafeString(
      item?.ioSafetyTimeRaw ?? item?.["iOSafetyTime(Weeks)(Rounded)"],
    );
  }

  const reasonVal = toSafeString(
    overrides?.reason ?? item?.["Reason"] ?? item?.reasonCode ?? "",
  );
  const commentsVal = toSafeString(
    overrides?.comments ?? item?.["Comments"] ?? item?.comments ?? "",
  );
  const getReasonCode = (choice: Choice, reasonVal: string) => {
    if (choice === "IO") {
      return "IO selected";
    } else {
      return normalizedReasonOptions.find((r) => r.value === reasonVal)?.label ?? ""
    }

  }
  const payload: any = {
    materialNumber: item?.materialNumber ?? item?.["Material Number"] ?? idSafe,
    materialPlant: item?.materialPlant ?? item?.["Material Plant"] ?? 5040, // default plant ID
    region: item?.region ?? "RG1", // default region
    userSelectedChoice: choice, // 'IO' or 'S4'
    userReasonCode: getReasonCode(choice, reasonVal), // Map choice to reason code
    userComments: commentsVal, // Comments from popup or default empty string
    userSafetyStockQty: toSafeNumber(submitSafetyStock), // Submit numeric value for safety stock qty
    userCoverageProfile: toSafeNumber(submitCoverage), // Submit numeric value for coverage profile
    userSafetyTime: toSafeNumber(submitSafetyTime), // Submit numeric value for safety time
    userConsolidatedSafetyStock:
      choice === "IO"
        ? toSafeNumber(item?.ioConsolidatedSafetyStockRaw ?? item?.["iOConsolidatedSS(weeks)"])
        : toSafeNumber(item?.s4ConsolidatedSafetyStockRaw ?? item?.["s4ConsolidatedSS(weeks)"]),
    varWkUserVsS4:
      choice === "IO" ? toSafeNumber(item?.["variationIOvsS4week"]) : 0,
    varPctUserVsS4:
      choice === "IO" ? toSafeNumber(item?.["variationIOvsS4%_numeric"]) : 0,
    approverId: item?.approverId,
    userId: localStorage.getItem("userId") || sessionStorage.getItem("userId") || "",
    ioExtractionDate: item.extractionDate
  };

  return payload;
};

const PopupWithCheckboxes: React.FC<PopupWithCheckboxesProps> = ({
  open,
  onClose,
  selectedRows,
  onSubmit,
  reasonOptions = [
    { value: "Maintain current S4 due to planning judgement" },
    { value: "IO appears outlier / data quality concern" },
    { value: "Stock risk if IO applied" },
    { value: "Lead time / variability change" },
    { value: "Other" },
  ],
  forceChoice,
}) => {
  const [selectedOption, setSelectedOption] = useState<Choice | "">(
    forceChoice ?? "",
  );
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [openSubmitToast, setOpenSubmitToast] = useState(false);
  // + NEW state (place with other useState declarations)
  const [normalizedReasonOptions, setNormalizedReasonOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [reasonsError, setReasonsError] = useState<string | null>(null);
  const [maxCoverage, setMaxCoverage] = useState<MaxCoverageLimit | null>(null);
  /** key → list of limit-violation messages for that row */
  const [limitErrors, setLimitErrors] = useState<Record<string, string[]>>({});
  const dispatch = useDispatch();
  const [submitToast, setSubmitToast] = useState<{
    open: boolean;
    message: string;
    status: number; // store HTTP status
  }>({ open: false, message: "", status: 0 });

  useEffect(() => {
    setSelectedOption(forceChoice ?? "");
  }, [forceChoice, open]);

  const { removeItem } = useArrayData();

  const [perRowInputs, setPerRowInputs] = useState<
    Record<string, { reason: string; comments: string; error?: boolean }>
  >({});

  const getKey = (r: any, idx: number) => {
    const material = String(
      r?.materialNumber ?? r?.["Material Number"] ?? r?.id ?? idx,
    ).trim();
    const plant = String(r?.materialPlant ?? r?.["Material Plant"] ?? "").trim();
    return `${material}__${plant}`;
  };
  // Fetch reason codes when dialog opens
  useEffect(() => {
    if (!open) return;

    let alive = true;
    (async () => {
      try {
        setReasonsLoading(true);
        setReasonsError(null);

        const apiList = await getReasonCodeList();

        const mapped = (apiList ?? []).map((r: any) => ({
          value: String(r.reasonCode),
          label: String(r.reasonDescription ?? r.reasonCode),
        }));

        if (alive) setNormalizedReasonOptions(mapped);
      } catch (e: any) {
        if (alive) setReasonsError("Failed to load reasons");
      } finally {
        if (alive) setReasonsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open]);

  // Fetch max-coverage limits when dialog opens
  useEffect(() => {
    if (!open || !selectedRows?.length) return;

    let alive = true;
    const plantId =
      selectedRows[0]?.materialPlant ??
      selectedRows[0]?.["Material Plant"] ??
      "";

    if (!plantId) return;

    (async () => {
      try {
        const limits = await getMaxCoverage(plantId);
        if (alive) {
          setMaxCoverage(limits);
          setLimitErrors({});
        }
      } catch {
        // non-blocking — limits won't be enforced if API fails
        if (alive) setMaxCoverage(null);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, selectedRows]);
  useEffect(() => {
    if (open) {
      // Reset the toast when popup is opened again
      setSubmitToast({
        open: false,
        message: "",
        status: 0,
      });
    }
    return () => {
      dispatch(setIsSubmitted(false));

    };
  }, [open]); // Reset when `open` prop changes

  useEffect(() => {
    const next: Record<string, { reason: string; comments: string }> = {};
    (selectedRows ?? []).forEach((r, idx) => {
      const k = getKey(r, idx);
      next[k] = {
        reason: toSafeString(r?.["Reason"] ?? r?.reasonCode ?? ""),
        comments: toSafeString(r?.["Comments"] ?? r?.comments ?? ""),
      };
    });
    setPerRowInputs(next);
  }, [open, selectedRows]);

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as Choice;
    setSelectedOption(value);
  };

  const handleReasonChange = (key: string, value: string) => {
    setPerRowInputs((prev) => ({
      ...prev,
      [key]: { ...prev[key], reason: value, error: !value },
    }));
  };

  const handleCommentsChange = (key: string, value: string) => {
    setPerRowInputs((prev) => ({
      ...prev,
      [key]: { ...prev[key], comments: value },
    }));
  };

  const allReasonsSelected =
    selectedOption !== "S4" ||
    (selectedRows ?? []).every(
      (r, idx) => !!perRowInputs[getKey(r, idx)]?.reason,
    );

  const handleSubmitClick = () => {
    setOpenSubmitToast(true);
  };

  // handleSubmit
  /** Validate each row's proposed values against the max-coverage limits.
   *  Returns true when all rows are within limits (or limits are unavailable).
   */
  const validateLimits = (): boolean => {
    if (!maxCoverage || !selectedOption) return true;

    const errors: Record<string, string[]> = {};

    (selectedRows ?? []).forEach((r, idx) => {
      const k = getKey(r, idx);
      const rowErrors: string[] = [];

      const safetyTime =
        selectedOption === "IO"
          ? toSafeNumber(r?.ioSafetyTimeRaw ?? r?.["iOSafetyTime(Weeks)(Rounded)"])
          : toSafeNumber(r?.s4SafetyTimeRaw ?? r?.["s4SafetyTime(weeks)"]);

      const coverageProfile =
        selectedOption === "IO"
          ? toSafeNumber(r?.ioCoverageProfileRaw ?? r?.["iOCoverageprofile(weeks)"])
          : toSafeNumber(r?.s4CoverageProfileRaw ?? r?.["s4Coverageprofile(weeks)"]);

      if (safetyTime > maxCoverage.safetyTime) {
        rowErrors.push(
          `Safety Time ${safetyTime} exceeds the limit of ${maxCoverage.safetyTime}`,
        );
      }
      if (coverageProfile > maxCoverage.coverageProfile) {
        rowErrors.push(
          `Coverage Profile ${coverageProfile} exceeds the limit of ${maxCoverage.coverageProfile}`,
        );
      }

      if (rowErrors.length) errors[k] = rowErrors;
    });

    setLimitErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!selectedOption) {
      onClose();
      return;
    }
    // Keep limit checks informational only; do not block submit/API call.
    validateLimits();
    if (selectedOption === "S4" && !allReasonsSelected) {
      const next = { ...perRowInputs };
      (selectedRows ?? []).forEach((r, idx) => {
        const k = getKey(r, idx);
        if (!next[k]?.reason)
          next[k] = {
            ...(next[k] ?? { reason: "", comments: "" }),
            error: true,
          };
      });
      setPerRowInputs(next);
      return;
    }

    const payloads = (selectedRows ?? [])
      .filter((r) => !!r)
      .map((r, idx) => {
        const k = getKey(r, idx);
        const overrides = perRowInputs[k] ?? { reason: "", comments: "" };
        return createPayloadFromChoice(
          r,
          selectedOption as Choice,
          idx,
          overrides,
          normalizedReasonOptions
        );
      });

    const inProgressId = enqueueSnackbar({
      message: 'Submitting proposals for material',
      severity: 'warning',
      persist: true,
      autoHideDuration: null,
    });
    onClose();

    try {
      let response = await submitPlannerView(payloads);
      removeItem(() => true);
      setPerRowInputs({});
      response = { ...response, skippedMaterials: response?.skippedMaterials || [] };
      if (response?.skippedMaterials?.length > 0) {
        onSubmit?.(selectedOption as Choice, payloads, response);
      } else {
        enqueueSnackbar({
          message: 'Enduser Submitted Successfully',
          severity: 'success',
          autoHideDuration: 2500,
        });
      }
      dispatch(setIsSubmitted(true));

      try {
        const existing = sessionStorage.getItem("approverRows");
        const prior = existing ? JSON.parse(existing) : [];
        const byId = new Map<string, any>();
        [...prior, ...payloads].forEach((row) => {
          const material = String(
            row?.id ?? row?.materialNumber ?? row?.["Material Number"] ?? "",
          ).trim();
          const plant = String(
            row?.materialPlant ?? row?.["Material Plant"] ?? "",
          ).trim();
          const key = `${material}__${plant}`;
          byId.set(key || `${Date.now()}-${Math.random()}`, row);
        });
        sessionStorage.setItem(
          "approverRows",
          JSON.stringify(Array.from(byId.values())),
        );
      } catch {
        /* ignore storage errors */
      }
    } catch (error) {
      enqueueSnackbar({
        message: getPlannerPricingError(error) ?? "Failed to Submit the Material",
        severity: 'error',
        autoHideDuration: 2500,
      });
    } finally {
      closeSnackbar(inProgressId);
    }
  };

  return (
    <Dialog
      open={ open }
      onClose={ onClose }
      maxWidth="md"

      sx={ {
        "& .MuiDialog-paper": {
          backgroundColor: "#fff",
        },
      } }
    >
      <DialogTitle>Select the following options</DialogTitle>

      <DialogContent>
        <Stack gap={ 1.5 }>
          { !forceChoice && (
            <RadioGroup
              row
              value={ selectedOption }
              onChange={ handleOptionChange }
            >
              <FormControlLabel
                value="IO"
                control={ <Radio /> }
                label="Accept IO proposal"
              />
              <FormControlLabel
                value="S4"
                control={ <Radio /> }
                label="Keep S4 values"
              />
            </RadioGroup>
          ) }

          { selectedOption === "S4" && (
            <Stack mt={ 2 } gap={ 1.5 }>
              <Typography variant="subtitle2">
                Provide Reason (required) and Comments (optional) for selected
                rows:
              </Typography>
              { selectedRows.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Material</TableCell>
                      <TableCell>Plant</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Reason *</TableCell>
                      <TableCell>Comments</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    { (selectedRows ?? []).map((r, idx) => {
                      const key = getKey(r, idx);
                      const values = perRowInputs[key] ?? {
                        reason: "",
                        comments: "",
                      };
                      const reasonError = !values.reason && values.error;

                      return (
                        <TableRow key={ key }>
                          <TableCell>
                            { String(
                              r?.materialNumber ?? r?.["Material Number"] ?? "",
                            ) }
                          </TableCell>
                          <TableCell>
                            { String(
                              r?.materialPlant ?? r?.["Material Plant"] ?? "",
                            ) }
                          </TableCell>
                          <TableCell>
                            { String(
                              r?.materialDescription ??
                              r?.["Material Description"] ??
                              "",
                            ) }
                          </TableCell>
                          <TableCell>
                            <FormControl
                              fullWidth
                              size="small"
                              error={ reasonError }
                            >
                              <InputLabel id={ `reason-label-${key}` }>
                                Reason
                              </InputLabel>
                              <Select
                                labelId={ `reason-label-${key}` }
                                value={ values.reason }         // stores reasonCode (e.g., "5")
                                label="Reason"
                                onChange={ (e) => handleReasonChange(key, String(e.target.value)) }
                                disabled={ reasonsLoading }     // optional UX
                              >
                                { normalizedReasonOptions.map((opt) => (
                                  <MenuItem key={ opt.value } value={ opt.value }>
                                    { opt.label }
                                  </MenuItem>
                                )) }
                              </Select>
                              { reasonsError && <FormHelperText error>{ reasonsError }</FormHelperText> }
                              { reasonError && (
                                <FormHelperText>
                                  Reason is required
                                </FormHelperText>
                              ) }
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Optional comments"
                              value={ values.comments }
                              onChange={ (e) =>
                                handleCommentsChange(key, e.target.value)
                              }
                            />
                          </TableCell>
                          { limitErrors[key]?.length > 0 && (
                            <TableCell colSpan={ 5 } sx={ { pb: 0 } }>
                              <Box sx={ { color: "error.main", fontSize: 12 } }>
                                { limitErrors[key].map((msg, i) => (
                                  <div key={ i }>⚠️ { msg }</div>
                                )) }
                              </Box>
                            </TableCell>
                          ) }
                        </TableRow>
                      );
                    }) }
                  </TableBody>
                </Table>
              ) }
            </Stack>
          ) }
          { selectedOption === "IO" && maxCoverage && (
            <Box
              sx={{
                display:
                  Object.keys(limitErrors).length > 0 ? "block" : "none",
                mt: 1,
                p: 1,
                border: "1px solid",
                borderColor: "error.main",
                borderRadius: 1,
                bgcolor: "#fff5f5",
              }}
            >
              <Typography variant="body2" color="error" fontWeight={600}>
                The following rows exceed the plant limits (Safety Time ≤
                { maxCoverage.safetyTime }, Coverage Profile ≤
                { maxCoverage.coverageProfile }):
              </Typography>
              { Object.entries(limitErrors).map(([key, msgs]) => (
                <Box key={ key } sx={{ mt: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>{ key }</Typography>
                  { msgs.map((m, i) => (
                    <Typography key={ i } variant="body2" color="error">
                      • { m }
                    </Typography>
                  )) }
                </Box>
              )) }
            </Box>
          ) }
        </Stack>
        <ErrorDialog
          open={ submitToast.open && submitToast.status !== 200 }
          message={ submitToast.message }
          onClose={ () => setSubmitToast({ ...submitToast, open: false }) }
        />
        <Snackbar
          open={ submitToast.open && submitToast.status === 200 }
          autoHideDuration={ 2000 }
          onClose={ () => setSubmitToast({ ...submitToast, open: false }) }
          anchorOrigin={ { vertical: "top", horizontal: "center" } }
        >
          <Alert
            onClose={ () => setSubmitToast({ ...submitToast, open: false }) }
            severity="success"
            variant="filled"
          >
            { submitToast.message }
          </Alert>
        </Snackbar>
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={ onClose }>
          Cancel
        </Button>
        <Button
          onClick={ handleSubmit }
          disabled={ !selectedOption && !forceChoice }
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PopupWithCheckboxes;
